import React, { useCallback, useContext, useMemo, useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

interface DialogAction {
  label: string;
  onClick: () => void;
}

interface DialogHelperOptions {
  title?: string;
  message?: string;
  actions?: DialogAction[];
}

interface DialogHelpers {
  alert: (options: DialogHelperOptions) => void;
  close: () => void;
  Actions: {
    Ok: DialogAction;
    Cancel: DialogAction;
  };
}

const GlobalDialogContext = React.createContext<DialogHelpers | null>(null);
GlobalDialogContext.displayName = "GlobalDialogContext";

export const GlobalDialogProvider: React.FC = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [actions, setActions] = useState<DialogAction[]>([]);

  const close = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      // run the fade animation before resetting the contents so that user won't
      // see the contents being blank while fading.
      setTitle("");
      setMessage("");
      setActions([]);
    }, 200);
  }, []);

  const Actions: DialogHelpers["Actions"] = {
    Ok: { label: "OK", onClick: close },
    Cancel: { label: "Cancel", onClick: close },
  };

  const alert = useCallback<DialogHelpers["alert"]>((options) => {
    setTitle(options.title ?? "");
    setMessage(options.message ?? "");
    setActions(options.actions ?? [Actions.Ok]);
    setIsOpen(true);
  }, []);

  const helpers = useMemo<DialogHelpers>(
    () => ({
      alert,
      close,
      Actions,
    }),
    []
  );

  return (
    <GlobalDialogContext.Provider value={helpers}>
      {children}
      <Dialog
        open={isOpen}
        onClose={close}
        aria-labelledby="global-dialog-title"
        aria-describedby="global-dialog-description"
      >
        <DialogTitle id="global-dialog-title">{title}</DialogTitle>
        <DialogContent>
          <DialogContentText id="global-dialog-description">
            {message}
          </DialogContentText>
          <DialogActions>
            {actions.map((action) => (
              <Button
                key={action.label}
                onClick={action.onClick}
                variant="text"
              >
                {action.label}
              </Button>
            ))}
          </DialogActions>
        </DialogContent>
      </Dialog>
    </GlobalDialogContext.Provider>
  );
};

export default function useGlobalDialog(): DialogHelpers {
  const value = useContext(GlobalDialogContext);

  if (value == null) throw new Error("GlobalDialogContext.Provider not found");

  return value;
}
